// Copyright (c) .NET Foundation. All rights reserved.
// Licensed under the Apache License, Version 2.0. See License.txt in the project root for license information.
// Copyright (c) .NET Foundation and Contributors
//
// All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License"); you may not use
// this file except in compliance with the License. You may obtain a copy of the
// License at
//
//    http://www.apache.org/licenses/LICENSE-2.0
//
//Unless required by applicable law or agreed to in writing, software distributed
//under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
//CONDITIONS OF ANY KIND, either express or implied. See the License for the
//specific language governing permissions and limitations under the License.

// NOTE
// If you update @microsoft/signalr, ensure you update this to match, Specifically taking note of ReviveDateTime!
/* eslint-disable */

import {
    IHubProtocol,
    HubMessage,
    TransferFormat,
    ILogger,
    NullLogger,
    MessageType,
    LogLevel,
    InvocationMessage,
    StreamItemMessage,
    CompletionMessage,
} from '@microsoft/signalr';
import { ReviveDateTime } from './JSON';

const JSON_HUB_PROTOCOL_NAME: string = 'json';

/** Implements the JSON Hub Protocol. */
export class JsonHubProtocol implements IHubProtocol {
    /** @inheritDoc */
    public readonly name: string = JSON_HUB_PROTOCOL_NAME;
    /** @inheritDoc */
    public readonly version: number = 1;

    /** @inheritDoc */
    public readonly transferFormat: TransferFormat = TransferFormat.Text;

    /** Creates an array of {@link @microsoft/signalr.HubMessage} objects from the specified serialized representation.
     *
     * @param {string} input A string containing the serialized representation.
     * @param {ILogger} logger A logger that will be used to log messages that occur during parsing.
     */
    public parseMessages(input: string, logger: ILogger): HubMessage[] {
        // The interface does allow "ArrayBuffer" to be passed in, but this implementation does not. So let's throw a useful error.
        if (typeof input !== 'string') {
            throw new Error('Invalid input for JSON hub protocol. Expected a string.');
        }

        if (!input) {
            return [];
        }

        if (logger === null) {
            logger = NullLogger.instance;
        }

        // Parse the messages
        const messages = TextMessageFormat.parse(input);

        const hubMessages = [];
        for (const message of messages) {
            const parsedMessage = JSON.parse(message, ReviveDateTime) as HubMessage;
            if (typeof parsedMessage.type !== 'number') {
                throw new Error('Invalid payload.');
            }
            switch (parsedMessage.type) {
                case MessageType.Invocation:
                    this._isInvocationMessage(parsedMessage);
                    break;
                case MessageType.StreamItem:
                    this._isStreamItemMessage(parsedMessage);
                    break;
                case MessageType.Completion:
                    this._isCompletionMessage(parsedMessage);
                    break;
                case MessageType.Ping:
                    // Single value, no need to validate
                    break;
                case MessageType.Close:
                    // All optional values, no need to validate
                    break;
                default:
                    // Future protocol changes can add message types, old clients can ignore them
                    logger.log(LogLevel.Information, "Unknown message type '" + parsedMessage.type + "' ignored.");
                    continue;
            }
            hubMessages.push(parsedMessage);
        }

        return hubMessages;
    }

    /** Writes the specified {@link @microsoft/signalr.HubMessage} to a string and returns it.
     *
     * @param {HubMessage} message The message to write.
     * @returns {string} A string containing the serialized representation of the message.
     */
    public writeMessage(message: HubMessage): string {
        return TextMessageFormat.write(JSON.stringify(message));
    }

    private _isInvocationMessage(message: InvocationMessage): void {
        this._assertNotEmptyString(message.target, 'Invalid payload for Invocation message.');

        if (message.invocationId !== undefined) {
            this._assertNotEmptyString(message.invocationId, 'Invalid payload for Invocation message.');
        }
    }

    private _isStreamItemMessage(message: StreamItemMessage): void {
        this._assertNotEmptyString(message.invocationId, 'Invalid payload for StreamItem message.');

        if (message.item === undefined) {
            throw new Error('Invalid payload for StreamItem message.');
        }
    }

    private _isCompletionMessage(message: CompletionMessage): void {
        if (message.result && message.error) {
            throw new Error('Invalid payload for Completion message.');
        }

        if (!message.result && message.error) {
            this._assertNotEmptyString(message.error, 'Invalid payload for Completion message.');
        }

        this._assertNotEmptyString(message.invocationId, 'Invalid payload for Completion message.');
    }

    private _assertNotEmptyString(value: any, errorMessage: string): void {
        if (typeof value !== 'string' || value === '') {
            throw new Error(errorMessage);
        }
    }
}

export class TextMessageFormat {
    public static RecordSeparatorCode = 0x1e;
    public static RecordSeparator = String.fromCharCode(TextMessageFormat.RecordSeparatorCode);

    public static write(output: string): string {
        return `${output}${TextMessageFormat.RecordSeparator}`;
    }

    public static parse(input: string): string[] {
        if (input[input.length - 1] !== TextMessageFormat.RecordSeparator) {
            throw new Error("Message is incomplete.");
        }

        const messages = input.split(TextMessageFormat.RecordSeparator);
        messages.pop();
        return messages;
    }
}